import React, { useState } from 'react';

/**
 * Issue #417: Shared modal for suspending a user account. Collects both
 * the suspension duration in days and a documented reason (>=20 chars,
 * stored in admin_audit_logs for 1 year). Used by the "Alle Benutzer"
 * list and the user detail page.
 *
 * Props:
 *   - open:           whether the modal is visible
 *   - userName:       display name of the target user (shown in heading)
 *   - onConfirm(days, reason): async handler
 *   - onCancel():     handler when the admin aborts
 *   - minLength:      minimum reason length (default 20)
 *   - defaultDays:    initial value of the days input (default 7)
 */
function SuspendUserModal({
  open,
  userName,
  onConfirm,
  onCancel,
  minLength = 20,
  defaultDays = 7,
}) {
  const [days, setDays] = useState(String(defaultDays));
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const trimmedLength = reason.trim().length;
  const daysNum = parseInt(days, 10);
  const isDaysValid = !isNaN(daysNum) && daysNum > 0;
  const isReasonValid = trimmedLength >= minLength;
  const isValid = isDaysValid && isReasonValid;

  const reset = () => {
    setDays(String(defaultDays));
    setReason('');
    setError('');
    setLoading(false);
  };

  const handleCancel = () => {
    reset();
    onCancel && onCancel();
  };

  const handleConfirm = async () => {
    if (!isDaysValid) {
      setError('Bitte eine gültige Anzahl von Tagen eingeben');
      return;
    }
    if (!isReasonValid) {
      setError(`Der Grund muss mindestens ${minLength} Zeichen lang sein`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(daysNum, reason.trim());
      reset();
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message || err?.message || 'Sperren fehlgeschlagen';
      setError(apiMessage);
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Konto sperren</h2>
        {userName && (
          <p>
            Sie sind dabei, das Konto von <strong>{userName}</strong> zu sperren.
          </p>
        )}
        <div className="form-group">
          <label>Für wie viele Tage sperren?</label>
          <input
            type="number"
            min="1"
            value={days}
            onChange={(e) => {
              setDays(e.target.value);
              if (error) setError('');
            }}
            className="filter-input"
            disabled={loading}
          />
        </div>
        <p>
          Bitte geben Sie einen Grund für die Sperre an (mindestens {minLength}{' '}
          Zeichen):
        </p>
        <div className="form-group">
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder="Grund für die Sperre eingeben..."
            className="filter-input delete-reason-input"
            rows={4}
            disabled={loading}
          />
          <div className="character-count">
            {trimmedLength}/{minLength} Zeichen
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>
        <div className="form-actions">
          <button
            className="secondary-button"
            onClick={handleCancel}
            disabled={loading}
          >
            Abbrechen
          </button>
          <button
            className="primary-button delete-confirm-button"
            onClick={handleConfirm}
            disabled={loading || !isValid}
          >
            Sperren
          </button>
        </div>
      </div>
    </div>
  );
}

export default SuspendUserModal;
